/**
 * Billing Page
 *
 * Main billing and usage dashboard with subscription management.
 * Migrated to Tailwind + shadcn/ui
 */

import React, { useState } from 'react';
import {
  CreditCard,
  Receipt,
  BarChart3,
  Settings,
  ExternalLink,
} from 'lucide-react';
import { useSubscription, useOpenPortal, useCancelSubscription } from '../../hooks';
import UsageDashboard from './UsageDashboard';
import SubscriptionCard from './SubscriptionCard';
import InvoiceList from './InvoiceList';
import PlanSelector from './PlanSelector';
import CancellationRetentionModal from './CancellationRetentionModal';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export default function BillingPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  const openPortalMutation = useOpenPortal();
  const cancelMutation = useCancelSubscription();

  const handleCancel = () => {
    if (subscription?.status === 'trialing' && subscription?.retention_offer_eligible) {
      // Show retention offer modal for trialing users
      setShowCancelModal(true);
    } else if (subscription) {
      // Standard cancel at period end for active subscribers
      if (window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.')) {
        cancelMutation.mutate(false);
      }
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'invoices', label: 'Invoices', icon: Receipt },
    { id: 'payment', label: 'Payment', icon: CreditCard },
  ];

  const handleManageBilling = () => {
    openPortalMutation.mutate(window.location.href);
  };

  return (
    <div className="max-w-[1200px] mx-auto">
      {/* Page Header */}
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <CreditCard className="w-8 h-8 text-teal-500" />
          <div>
            <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
              Billing & Usage
            </h1>
            <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
              Manage your subscription, view usage, and download invoices
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <Button
            variant="secondary"
            onClick={handleManageBilling}
            disabled={openPortalMutation.isPending}
          >
            <Settings size={16} />
            {openPortalMutation.isPending ? 'Opening...' : 'Manage in Stripe'}
            <ExternalLink size={16} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-grey-100 dark:bg-grey-800 rounded-xl mb-6 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-teal-500 text-white'
                  : 'text-grey-500 dark:text-grey-400 hover:text-grey-900 dark:hover:text-grey-50 hover:bg-grey-200 dark:hover:bg-grey-700'
              )}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="grid gap-6">
          <SubscriptionCard
            subscription={subscription}
            isLoading={subscriptionLoading}
            onUpgrade={() => setShowPlanSelector(true)}
            onCancel={handleCancel}
          />
          <UsageDashboard />
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="grid gap-6">
          <InvoiceList />
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="grid gap-6">
          <div>
            <h3 className="text-base font-semibold text-grey-900 dark:text-grey-50 mb-4">
              Payment Methods
            </h3>
            <p className="text-sm text-grey-500 dark:text-grey-400">
              Manage your payment methods through the Stripe customer portal.
            </p>
            <Button
              variant="secondary"
              onClick={handleManageBilling}
              className="mt-4"
            >
              <CreditCard size={16} />
              Manage Payment Methods
              <ExternalLink size={16} />
            </Button>
          </div>
        </div>
      )}

      {showPlanSelector && (
        <PlanSelector
          currentPlanId={subscription?.plan_id}
          onClose={() => setShowPlanSelector(false)}
        />
      )}

      {showCancelModal && (
        <CancellationRetentionModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          subscription={subscription}
        />
      )}
    </div>
  );
}
